import React, {useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {isOk} from '../../utils/apiResponse';
import {DISMISS_CAMPAIGN, UncontrolledSpendApi} from '../../api/uncontrolledSpendApi';
import {UncoveredSpendApi} from '../../api/uncoveredSpendApi';
import {campaignKey} from './uncoveredSpend';
import {currentSession, useResolutionActions} from './uncoveredResolutions';

const RAW = {interpolation: {escapeValue: false}};
// El descarte es para siempre: el toast que lo confirma deja deshacerlo un rato más que los demás.
const UNDO_TOAST_MS = 8000;
// «Ya estaba silenciada» (`AlertSnooze.new_with`, que el backend manda como código en `status`): otra
// pestaña u otra persona la descartó antes. Para quien la marca es lo mismo que haberlo hecho.
const ALREADY_SNOOZED = 'ERROR_ALREADY_SNOOZED';

export const GENERIC_ERROR_KEY = 'alerts_center.uncontrolled.error.generic';

function codeOf(response) {
    return response && (response.error_code || response.status);
}

function dismissed(response) {
    return isOk(response) || codeOf(response) === ALREADY_SNOOZED;
}

/**
 * El toast de «No hace falta controlarla», con «Deshacer». El descarte es para siempre: en la 2.1 se vuelve
 * atrás desde «Lo que dejaste de mostrar» de «Inversión sin control» (que desde UD-5 tiene toda la 2.1),
 * pero en el clásico no hay otra pantalla, así que el toast es la única vuelta (review C-cl-fe-linea-03).
 */
export function DismissedToast({name, onUndo}) {
    const {t} = useTranslation();
    const [undoing, setUndoing] = useState(false);
    const undo = () => {
        if (undoing) return;
        setUndoing(true);
        onUndo();
    };
    return (
        <span className="usp-toast">
            <span>{t('line_uncovered.dismissed_toast', {name, ...RAW})}</span>
            <button type="button" className="usp-toast__undo" onClick={undo} disabled={undoing}>
                {t('line_uncovered.undo')}
            </button>
        </span>
    );
}

/**
 * Las acciones del popover de la línea sobre lo que su regla deja afuera: «No hace falta controlarla» (con
 * «Deshacer»). Una a la vez (`busyKey`: `dismiss:<campaña>`). Lo que el backend confirma se marca resuelto
 * para toda la pestaña (`uncoveredResolutions.js`): sale en el acto del chip y del popover, también de la
 * fuga de otra línea que nombre la misma campaña. «Sumar a una línea» vive en «Inversión sin control» (UD-5).
 */
export function useUncoveredActions() {
    const {t} = useTranslation();
    const {mark: markResolved, unmark: unmarkResolved} = useResolutionActions();
    const [busyKey, setBusyKey] = useState(null);
    // El guardia va en un ref: dos clics en el mismo tick verían el `busyKey` viejo del render.
    const busy = useRef(false);

    const run = useCallback((key, request) => {
        if (busy.current) return Promise.resolve(false);
        busy.current = true;
        setBusyKey(key);
        return request().finally(() => {
            busy.current = false;
            setBusyKey(null);
        });
    }, []);

    const undoDismiss = useCallback((campaign) => UncontrolledSpendApi.undoDismiss({
        indicator: DISMISS_CAMPAIGN, channel: campaign.channel, accountId: campaign.account_id,
        campaignId: campaign.campaign_id,
    }).then((response) => {
        if (!isOk(response)) {
            toast.error(t(GENERIC_ERROR_KEY));
            return;
        }
        UncoveredSpendApi.clearCache();
        unmarkResolved([campaignKey(campaign)]);
        toast.info(t('line_uncovered.undone_toast', {name: campaign.name, ...RAW}));
    }), [t, unmarkResolved]);

    // La sesión del pedido viaja hasta la respuesta: si cambió mientras tanto (logout), no se marca nada
    // (review C-cs-fe-02).
    const dismiss = useCallback((campaign) => run(`dismiss:${campaignKey(campaign)}`, () => {
        const session = currentSession();
        return UncontrolledSpendApi.dismiss({
            indicator: DISMISS_CAMPAIGN, channel: campaign.channel, accountId: campaign.account_id,
            campaignId: campaign.campaign_id,
        }).then((response) => {
            if (!dismissed(response)) {
                toast.error(t(GENERIC_ERROR_KEY));
                return false;
            }
            UncoveredSpendApi.clearCache();
            markResolved([campaignKey(campaign)], session);
            toast.success(({closeToast}) => (
                <DismissedToast name={campaign.name}
                                onUndo={() => undoDismiss(campaign).finally(() => closeToast && closeToast())}/>
            ), {autoClose: UNDO_TOAST_MS});
            return true;
        });
    }), [run, t, markResolved, undoDismiss]);

    return {busyKey, dismiss};
}

export default useUncoveredActions;
